/**
 * Localization (ADR 0017): English is the source and default, Bahasa Melayu secondary, and a
 * generated pseudo-locale for visual tests. ICU message format. One namespace per feature.
 * Locale resolution: user setting -> browser language -> English. Missing `ms` keys fall back
 * to English.
 */
import i18next, { type Resource } from "i18next";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";

import { pseudoLocalize } from "./pseudo";

export const SUPPORTED_LOCALES = ["en", "ms"] as const;
export const PSEUDO_LOCALE = "pseudo";
export type Locale = (typeof SUPPORTED_LOCALES)[number] | typeof PSEUDO_LOCALE;
export const NAMESPACES = [
  "common",
  "nav",
  "shell",
  "role",
  "home",
  "honest",
  "launcher",
  "palette",
  "settings",
  "auth",
  "status",
  "error",
  "products",
  "admin",
  "requests",
  "dashboard",
  "activity",
  "reason",
  "notification",
  "projects",
  "karya",
  "plan",
  "studio",
  "production",
  "assets",
  "editor",
  "review",
  "package",
] as const;

type Catalog = Record<string, unknown>;
const enFiles = import.meta.glob<Catalog>("./locales/en/*.json", { eager: true, import: "default" });
const msFiles = import.meta.glob<Catalog>("./locales/ms/*.json", { eager: true, import: "default" });

function byNamespace(files: Record<string, Catalog>): Record<string, Catalog> {
  const out: Record<string, Catalog> = {};
  for (const [path, body] of Object.entries(files)) {
    const ns = (path.split("/").pop() ?? path).replace(".json", "");
    out[ns] = body;
  }
  return out;
}

// Catalog files are split per feature namespace; at runtime they form one tree so keys read
// `feature.screen.element` (ADR 0017 rule 12), e.g. t("nav.dashboard").
const en = byNamespace(enFiles);
const resources: Resource = {
  en: { translation: en },
  ms: { translation: byNamespace(msFiles) },
  [PSEUDO_LOCALE]: { translation: pseudoLocalize(en) },
};

const STORAGE_KEY = "prisma.locale";

export function resolveInitialLocale(userLocale?: string | null): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get("lng");
  const stored = safeStorageGet(STORAGE_KEY);
  const candidates = [fromUrl, userLocale, stored, ...navigator.languages.map((l) => l.slice(0, 2))];
  for (const c of candidates) {
    if (c === PSEUDO_LOCALE || (SUPPORTED_LOCALES as readonly string[]).includes(c ?? "")) {
      return c as Locale;
    }
  }
  return "en";
}

export function safeStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode: preference simply is not remembered */
  }
}

export async function initI18n(locale: Locale = resolveInitialLocale()): Promise<typeof i18next> {
  await i18next
    .use(ICU)
    .use(initReactI18next)
    .init({
      resources,
      lng: locale,
      fallbackLng: "en",
      defaultNS: "translation",
      nsSeparator: false,
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  document.documentElement.lang = locale === PSEUDO_LOCALE ? "en-XA" : locale;
  return i18next;
}

export async function changeLocale(locale: Locale): Promise<void> {
  await i18next.changeLanguage(locale);
  document.documentElement.lang = locale === PSEUDO_LOCALE ? "en-XA" : locale;
  safeStorageSet(STORAGE_KEY, locale);
}

/** Intl locale for formatting (ADR 0017 rule 5). */
export function intlLocale(locale: string): string {
  return locale === "ms" ? "ms-MY" : "en-MY";
}
